import React from "react";
import {
  Box,
  Paper,
  Skeleton,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import { keyframes } from "@mui/material/styles";

import { useState, useEffect } from "react";
import { Breadcrumbs, Link } from "@mui/material";

const ViewerContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(2),
  borderRadius: "12px",
  backgroundColor: "#fff",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  minHeight: "800px",
  position: "relative",
}));

const LoaderOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1,
  borderRadius: "12px",
}));

const DocumentHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const DocumentContent = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
}));

const SidePanel = styled(Box)(({ theme }) => ({
  width: "250px",
  borderRight: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
}));

const MainContent = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
}));

const LoadingIcon = styled(DocumentScannerIcon)(({ theme }) => ({
  fontSize: "48px",
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  animation: "pulse 1.5s infinite",
  "@keyframes pulse": {
    "0%": {
      opacity: 0.6,
      transform: "scale(0.95)",
    },
    "50%": {
      opacity: 1,
      transform: "scale(1.05)",
    },
    "100%": {
      opacity: 0.6,
      transform: "scale(0.95)",
    },
  },
}));

const LegalDocumentViewer = ({ isLoading }) => {
  const theme = useTheme();

  const renderLoader = () => (
    <LoaderOverlay>
      <LoadingIcon />
      <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
        Loading Document
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Please wait while we prepare your document
      </Typography>
    </LoaderOverlay>
  );

  const renderSkeletonContent = () => (
    <>
      <DocumentHeader>
        <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="text" width="40%" height={24} />
        </Box>
      </DocumentHeader>

      <DocumentContent>
        <SidePanel>
          <Skeleton variant="text" width="80%" height={32} sx={{ mb: 2 }} />
          {[...Array(6)].map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              width="100%"
              height={40}
              sx={{ mb: 1, borderRadius: 1 }}
            />
          ))}
        </SidePanel>

        <MainContent>
          <Box sx={{ mb: 4 }}>
            <Skeleton variant="text" width="90%" height={28} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="70%" height={28} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="80%" height={28} />
          </Box>

          {[...Array(4)].map((_, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Skeleton variant="text" width="40%" height={24} sx={{ mb: 1 }} />
              <Skeleton
                variant="text"
                width="100%"
                height={20}
                sx={{ mb: 0.5 }}
              />
              <Skeleton
                variant="text"
                width="95%"
                height={20}
                sx={{ mb: 0.5 }}
              />
              <Skeleton variant="text" width="90%" height={20} />
            </Box>
          ))}

          <Box sx={{ mt: 4 }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={200}
              sx={{ borderRadius: 1 }}
            />
          </Box>
        </MainContent>
      </DocumentContent>
    </>
  );

  return (
    <ViewerContainer>
      {isLoading && renderLoader()}
      {renderSkeletonContent()}
    </ViewerContainer>
  );
};

export default LegalDocumentViewer;

export const pulseAnimation = keyframes`
  0% {
    transform: scale(0.95);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.6;
  }
`;

export const shimmerAnimation = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const DocumentPage = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate document loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link color="inherit" href="/">
          Home
        </Link>
        <Link color="inherit" href="/documents">
          Documents
        </Link>
        <Typography color="textPrimary">Legal Document</Typography>
      </Breadcrumbs>

      <LegalDocumentViewer isLoading={isLoading} />
    </Box>
  );
};
